import { ExclamationCircleIcon, LinkIcon } from '@heroicons/react/24/outline';
import {
  ariaDescribedByIds,
  examplesId,
  FormContextType,
  GenericObjectType,
  getInputProps,
  RJSFSchema,
  StrictRJSFSchema,
  WidgetProps,
} from '@rjsf/utils';
import clsx from 'clsx';

export function BaseInput<
  T = any,
  S extends StrictRJSFSchema = RJSFSchema,
  F extends FormContextType = any
>({
  schema,
  id,
  options,
  label,
  value,
  type,
  placeholder,
  required,
  disabled,
  readonly,
  autofocus,
  onChange,
  onBlur,
  onFocus,
  rawErrors,
  hideError,
  uiSchema,
  registry,
  formContext,
  hideLabel,
  ...rest
}: WidgetProps<T, S, F>) {
  const handleTextChange = ({
    target: { value: val },
  }: React.ChangeEvent<HTMLInputElement>) => {
    // Use the options.emptyValue if it is specified and newVal is also an empty string
    onChange(val === '' ? options.emptyValue || '' : val);
  };
  const handleBlur = ({
    target: { value: val },
  }: React.FocusEvent<HTMLInputElement>) => onBlur(id, val);
  const handleFocus = ({
    target: { value: val },
  }: React.FocusEvent<HTMLInputElement>) => onFocus(id, val);

  const inputProps = { ...rest, ...getInputProps(schema, type, options) };
  const { readonlyAsDisabled = true } = formContext as GenericObjectType;
  const hasError = rawErrors && rawErrors.length > 0 && !hideError;

  const className = clsx(
    'block w-full rounded-md shadow-sm focus:outline-none sm:text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-1 placeholder:text-gray-400 dark:placeholder:text-gray-500 border border-gray-300 dark:border-gray-600',
    !hasError &&
      !disabled &&
      !readonly &&
      'ring-gray-300 dark:ring-gray-600 focus:ring-2 focus:ring-indigo-600 dark:focus:ring-indigo-400',
    hasError &&
      'ring-red-300 dark:ring-red-500 pr-10 text-red-900 dark:text-red-100 placeholder-red-300 dark:placeholder-red-400 focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400'
  );

  if (options.comment) {
    return (
      <div className="text-xs text-gray-500 dark:text-gray-400">{value}</div>
    );
  }

  if (options.url) {
    return (
      <div>
        <a
          href={value}
          className="text-xs text-indigo-500 dark:text-indigo-400 hover:underline break-all flex items-base gap-1"
          target="_blank"
        >
          <LinkIcon className="h-4 w-4" />
          {value}
        </a>
      </div>
    );
  }

  if (options.threshold) {
    const className = clsx(
      'inline-flex items-baseline rounded-full px-2.5 py-0.5 text-sm font-medium ',
      value >= options.threshold
        ? 'bg-green-100 dark:bg-green-900/20 text-green-800 dark:text-green-400'
        : 'bg-red-100 dark:bg-red-900/20 text-red-800 dark:text-red-400'
    );

    return <div className={className}>{value} / 10</div>;
  }

  return (
    <div>
      <div className="relative">
        <input
          className={className}
          disabled={disabled || (readonlyAsDisabled && readonly)}
          id={id}
          onBlur={!readonly ? handleBlur : undefined}
          onChange={!readonly ? handleTextChange : undefined}
          onFocus={!readonly ? handleFocus : undefined}
          placeholder={placeholder}
          list={schema.examples ? examplesId<T>(id) : undefined}
          {...inputProps}
          defaultValue={value}
          aria-describedby={ariaDescribedByIds<T>(id, !!schema.examples)}
        />
        {hasError ? (
          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
            <ExclamationCircleIcon
              className="h-5 w-5 text-red-500 dark:text-red-400"
              aria-hidden="true"
            />
          </div>
        ) : null}
      </div>
      {Array.isArray(schema.examples) && (
        <datalist id={examplesId<T>(id)}>
          {(schema.examples as string[])
            .concat(
              schema.default && !schema.examples.includes(schema.default)
                ? ([schema.default] as string[])
                : []
            )
            .map((example) => {
              return <option key={example} value={example} />;
            })}
        </datalist>
      )}
    </div>
  );
}
